/**
 * Rotate (replace) a secret value.
 *
 * Shown behind an explicit confirmation step. The previous value is never
 * displayed; we only accept a new value and call PUT /secrets/{id}/value.
 */

import { FormEvent, useState, useEffect } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/i18n';
import { useRotateSecret } from '../hooks/use-secrets';
import type { Secret } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  secret: Secret | null;
}

export function SecretRotateModal({ open, onClose, secret }: Props) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty = open && value !== '';
  const discardLabels = {
    title: t('common.discardChanges.title') ?? 'Discard changes?',
    message: t('common.discardChanges.message') ?? 'You have unsaved changes. If you close now, they will be lost.',
    confirm: t('common.discardChanges.discard') ?? 'Discard',
    cancel: t('common.discardChanges.keepEditing') ?? 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };
  const rotate = useRotateSecret();

  useEffect(() => {
    if (!open) {
      setValue('');
      setShowValue(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!secret) return;

    if (!value) {
      setError(t('admin.secrets.form.errors.valueRequired') || 'Value is required');
      return;
    }

    try {
      await rotate.mutateAsync({ id: secret.id, input: { value } });
      setValue('');
      setShowValue(false);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('admin.secrets.form.errors.generic') || 'Could not rotate secret';
      setError(message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>
            {t('admin.secrets.rotate.title') || 'Rotate secret value'}
          </DialogTitle>
          <DialogDescription>
            {t('admin.secrets.rotate.description') ||
              'Replace the stored value with a new one. Consumers using a reference to this secret will immediately pick up the new value.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {secret && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <div className="font-medium">{secret.name}</div>
              {secret.description && (
                <div className="text-muted-foreground mt-1">{secret.description}</div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="rotate-value">
              {t('admin.secrets.form.newValue') || 'New value'}
            </Label>
            <div className="flex gap-2">
              <Input
                id="rotate-value"
                type={showValue ? 'text' : 'password'}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                required
                autoComplete="off"
                spellCheck={false}
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowValue((prev) => !prev)}
                aria-label={
                  showValue
                    ? t('admin.secrets.form.hideValue') || 'Hide value'
                    : t('admin.secrets.form.showValue') || 'Show value'
                }
              >
                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={rotate.isPending}
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={rotate.isPending}>
              {rotate.isPending
                ? t('common.saving') || 'Saving…'
                : t('admin.secrets.rotate.submit') || 'Rotate value'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}
